/**
 * Template Builder Chat Component
 *
 * Chat sidebar for Template Build feature. Handles message display, input, and user interactions.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { EmptyState, InputFooter, MessageBubble, ScrollContainer } from '@components/ui/chat';
import type { ActivityMessage, AssistantStatus, ChatMessage } from '@features/ai-chat-core';
import { useMessageHover } from '@hooks/useMessageHover';
import { ArrowUpward as SendIcon } from '@mui/icons-material';
import { Alert, Box, IconButton, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  clearError,
  initializeTemplateBuild,
} from '../store/chatSlice';
import {
  selectActivitiesArray,
  selectCurrentStep,
  selectError,
  selectFirstTokenReceived,
  selectInterrupt,
  selectSelectedSectionId,
  selectStreamingContent,
} from '../store/selectors';

import { TemplateActivityBubble } from './TemplateActivityBubble';
import { TemplateQuickReplyArea } from './TemplateQuickReplyArea';

export interface TemplateBuilderChatProps {
  /**
   * Task ID for context
   */
  taskId: string | null;

  /**
   * Chat messages to display
   */
  messages: ChatMessage[];

  /**
   * Current assistant status
   */
  assistantStatus: AssistantStatus;

  /**
   * Callback when user sends a message
   */
  onSendMessage: (message: string) => void;

  /**
   * Callback when user selects options in an interrupt
   */
  onSelectOptions: (selectedIds: string[]) => void;

  /**
   * Callback when user confirms/rejects in an interrupt
   */
  onConfirm?: (confirmed: boolean) => void;

  /**
   * Whether to hide the header
   */
  hideHeader?: boolean;

  /**
   * Optional callback when chat is cleared
   */
  onClearChat?: () => void;
}

/**
 * TemplateBuilderChat - Chat component for AI-powered template building
 */
export const TemplateBuilderChat: React.FC<TemplateBuilderChatProps> = ({
  taskId,
  messages,
  assistantStatus,
  onSendMessage,
  onSelectOptions,
  onConfirm,
  hideHeader = false,
  onClearChat: onClearChatProp,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { templateBuild } = useEventTracking();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');

  // Selectors
  const activities = useAppSelector(selectActivitiesArray);
  const streamingContent = useAppSelector(selectStreamingContent);
  const currentStep = useAppSelector(selectCurrentStep);
  const interrupt = useAppSelector(selectInterrupt);
  const error = useAppSelector(selectError);
  const firstTokenReceived = useAppSelector(selectFirstTokenReceived);
  const selectedSectionId = useAppSelector(selectSelectedSectionId);

  // Message hover hook
  const { hoveredMessageId, handleMouseEnter, handleMouseLeave } = useMessageHover();

  // Status flags
  // Note: 'awaiting_confirmation' and 'awaiting_selection' mean we're waiting for USER input,
  // not that the system is processing. Only disable when AI is actively working.
  const isProcessing =
    assistantStatus === 'thinking' ||
    assistantStatus === 'streaming';

  const showTypingIndicator =
    assistantStatus === 'thinking' ||
    (assistantStatus === 'streaming' && !firstTokenReceived);

  const hasActiveInterrupt = interrupt?.isActive === true;

  // Handle send message
  const handleSendMessage = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || isProcessing) return;

    // Track message sent
    if (taskId) {
      templateBuild.messageSent(taskId, {
        message_length: trimmedValue.length,
        has_section_context: !!selectedSectionId,
      });
    }

    onSendMessage(trimmedValue);
    setInputValue('');
  }, [inputValue, isProcessing, onSendMessage, taskId, templateBuild, selectedSectionId]);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Chat cannot be dismissed in template builder

  // Handle dismiss interrupt
  const handleDismiss = useCallback(() => {
    // Dismiss is handled in the quick reply area
  }, []);

  // Build message list with activities
  const messageList = useMemo(() => {
    const items: React.ReactNode[] = [];

    // Add messages with message bubbles
    messages.forEach(msg => {
      items.push(
        <MessageBubble
          key={msg.id}
          id={msg.id}
          role={msg.role}
          content={msg.content}
          timestamp={msg.timestamp}
          isHovered={hoveredMessageId === msg.id}
          onMouseEnter={() => handleMouseEnter(msg.id)}
          onMouseLeave={handleMouseLeave}
        />,
      );
    });

    // Add streaming content if available
    if (streamingContent) {
      const assistantRole = 'assistant' as const;
      items.push(
        <MessageBubble
          key="streaming"
          id="streaming"
          role={assistantRole}
          content={streamingContent}
          timestamp={new Date().toISOString()}
          isStreaming
        />,
      );
    }

    // Add activity bubbles
    activities.forEach((activity: ActivityMessage) => {
      items.push(
        <TemplateActivityBubble key={activity.messageId} activity={activity} />,
      );
    });

    // Add typing indicator
    if (showTypingIndicator) {
      items.push(
        <Box
          key="typing"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
            }}
          >
            {[0, 1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  opacity: 0.5,
                  animation: 'pulse 1.4s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                    '50%': { opacity: 1, transform: 'scale(1)' },
                  },
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {currentStep || 'Thinking...'}
          </Typography>
        </Box>,
      );
    }

    return items;
  }, [
    messages,
    streamingContent,
    activities,
    showTypingIndicator,
    currentStep,
    hoveredMessageId,
    handleMouseEnter,
    handleMouseLeave,
    theme,
  ]);

  // Handle clear chat
  const _handleClearChat = useCallback(() => {
    if (taskId) {
      dispatch(initializeTemplateBuild({ taskId, threadId: null }));
    }
    onClearChatProp?.();
  }, [dispatch, taskId, onClearChatProp]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            AI Template Builder
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <ScrollContainer
        className="template-build-messages"
        emptyState={
          <EmptyState
            title="AI Template Builder"
            description="I'll help you create a note template. You can ask me to add sections, modify existing ones, or adjust the structure."
            examples={[
              'Add a section for patient history',
              'Rename the first section to "Chief Complaint"',
              'Add an example output for the assessment section',
            ]}
          />
        }
      >
        {messageList}
      </ScrollContainer>

      {/* Quick Reply Area */}
      <TemplateQuickReplyArea
        interrupt={interrupt}
        onContinue={onSelectOptions}
        onConfirm={onConfirm}
        onDismiss={handleDismiss}
        isProcessing={isProcessing}
      />

      {/* Input Footer */}
      <InputFooter elevation>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            multiline
            maxRows={4}
            fullWidth
            placeholder={
              hasActiveInterrupt
                ? 'Respond to the prompt above...'
                : 'Ask me to modify your template...'
            }
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isProcessing && !hasActiveInterrupt}
            size="small"
            sx={{
              flex: 1,
              minWidth: 0,
              width: 'auto',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            aria-label="Send template message"
            disabled={!inputValue.trim() || (isProcessing && !hasActiveInterrupt)}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                bgcolor: theme.palette.grey[300],
                color: theme.palette.grey[500],
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </InputFooter>
    </Box>
  );
};
